import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { setPreference, usePreference } from '@fs/zion-preferences'

const DARK_MODE_PREFERENCE_KEY = 'shared-image-viewer.darkMode'

const DarkModeContext = createContext(undefined)

export default function DarkModeProvider({ children }) {
  const [isDarkModeOn, setIsDarkModeOn] = useState()
  const [isDarkModeAllowedOnPage, setIsDarkModeAllowedOnPage] = useState(false)
  const initialDarkModePreference = usePreference(DARK_MODE_PREFERENCE_KEY)

  useEffect(() => {
    if (typeof initialDarkModePreference === 'boolean') {
      setIsDarkModeOn(initialDarkModePreference)
    } else {
      setIsDarkModeOn(false)
    }
  }, [initialDarkModePreference])

  const setDarkMode = useCallback(async (isOn) => {
    setIsDarkModeOn(isOn)

    try {
      await setPreference(DARK_MODE_PREFERENCE_KEY, isOn)
    } catch (error) {
      console.error('Failed to persist dark mode preference', error)
    }
  }, [])

  const resetDarkMode = useCallback(async () => {
    setIsDarkModeOn(false)

    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(DARK_MODE_PREFERENCE_KEY)
    }

    try {
      await setPreference(DARK_MODE_PREFERENCE_KEY, false)
    } catch (error) {
      console.error('Failed to reset dark mode preference', error)
    }
  }, [])

  const theme = isDarkModeOn && isDarkModeAllowedOnPage ? 'night' : 'day'

  const value = useMemo(
    () => ({
      isDarkModeOn,
      loading: isDarkModeOn === undefined,
      setIsDarkModeOn: setDarkMode,
      resetDarkMode,
      theme,
      setIsDarkModeAllowedOnPage,
    }),
    [isDarkModeOn, resetDarkMode, setDarkMode, theme]
  )

  return <DarkModeContext.Provider value={value}>{children}</DarkModeContext.Provider>
}

export function useDarkModeContext() {
  const context = useContext(DarkModeContext)
  if (!context) {
    throw new Error(
      'useDarkModeContext must be used within a DarkModeProvider. If writing a test, use <StateProvider> from testUtils.js'
    )
  }
  return context
}

export { DarkModeContext }
